/* eslint-disable @atlaskit/platform/no-preconditioning -- Existing stream processing gates feature behavior from runtime flags. */

import type { Action } from 'react-sweet-state';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { isUIAnalyticsEvent } from '@atlaskit/analytics-next/isUIAnalyticsEvent';
import { getDocument } from '@atlaskit/browser-apis';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { isForgeAgentByCreatorType } from '@atlaskit/rovo-agent-components/common/utils/is-forge-agent';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type { Metric } from '@atlassian/browser-metrics/metric';
import { isRemixGeneratedFlow } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { getConfluenceResultAcceptanceAttributesForAskQuestion } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions/get-confluence-result-acceptance-attributes-for-ask-question';
import type { RovoApp } from '@atlassian/conversation-assistant-chat-prompt-input/controllers/chat-mode-store/types';
import { type MessageAgentIdentity } from '@atlassian/conversation-assistant-chat-prompt-input/ui/chat-prompt-input/message-agent-identity';
import { is3pApp } from '@atlassian/conversation-assistant-chat-prompt-input/utils/is-3p-app';
import { type CreationContextParams } from '@atlassian/conversation-assistant-external-context/types';
import {
	type FollowUpSource,
	type InteractionSource,
} from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import {
	ExperienceName,
	type ExperienceNameType,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { isRovoJourneyAllowlistedEvent } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type ExperienceTrackerAPI } from '@atlassian/conversation-assistant-instrumentation/main';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { type TrackAIEventPayloads } from '@atlassian/conversation-assistant-instrumentation/types/analytics';
import { mciKrAttributesFromSkillsAndPlugins } from '@atlassian/conversation-assistant-jsm-mci-kr/mci-kr-attributes-from-skills-and-plugins';
import { ASSISTANCE_SERVICE_ERROR_CODES } from '@atlassian/conversation-assistant-service-api/constants';
import { ROVO_CHAT_SITES_SOURCE_FILTER_GATE } from '@atlassian/conversation-assistant-service-api/feature-gates';
import {
	type Agent,
	type FetchStreamReturn,
	type FileUploadResponseItem,
	type HumanMessageDisplayContext,
	isChannelNotFoundError,
	type MessageType,
	type ParsedSSEMessage,
	type StreamActionError,
	type StreamError,
	type StreamMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { resolveMessageStreamBrowserUrl } from '@atlassian/conversation-assistant-service/services/assistance-service/resolve-message-stream-browser-url';
import { generateConversationName } from '@atlassian/conversation-assistant-service/services/assistance-service/utils/conversation';
import {
	beginAgentRun,
	consumeAgentRunInvocation,
} from '@atlassian/conversation-assistant-store/controllers/agent-mention-run-state';
import { type Store as ChatContextStore } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import {
	createProvisionalConversationAgent,
	resolveConversationAgentIdForRouting,
} from '@atlassian/conversation-assistant-store/controllers/chat/conversation-agent-identity';
import { deriveAgentSendRequestIdentity } from '@atlassian/conversation-assistant-store/controllers/chat/send-request-agent-identity';
import {
	type AgentMetadata,
	CHAT_STORE_STATUS,
	type ChatStorePrivateState,
	type Conversation,
	isStoreHuman,
	type StoreGuardBlock,
	type StoreHuman,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { setConversationCancelled } from '@atlassian/conversation-assistant-store/controllers/conversation-cancellation';
import {
	ROVO_AGENT_NAMED_ID,
	ROVO_AGENT_UUID,
} from '@atlassian/conversation-assistant-utils/default-agent';

import { messageLatencyMetrics } from '../../common/utils/browser-metrics/messageLatencyMetrics';
import { extractContentFeatures } from '../../common/utils/content-features/extractContentFeatures';
import { recordValidMessage } from '../../common/utils/csat-eligibility-storage/recordValidMessage';
import { randomIntFromInterval } from '../../common/utils/id-generator';
import { isRovoCustomAgent } from '../../common/utils/is-rovo-custom-agent';
import { DEFAULT_AGENT } from '../agents/constants';
import { getPromptContextDisplayState } from '../analytics/get-prompt-context-display-state';
import { getSelectedWorkItemCountBucket } from '../analytics/prompt-context-analytics';
import { StreamLifecycleState } from '../bridge-api/streaming-lifecycle/StreamLifecycleState';
import { persistRecentConversation } from '../recent-conversation-persistence/recentConversationPersistence';
import { buildOptimisticSelectedWorkDisplayContext } from './build-optimistic-selected-work-display-context';
import { buildUnifiedStreamTaskTarget } from './buildUnifiedStreamTaskTarget';
import { clearStatus } from './clearStatus';
import { clearTracesIfNotEmpty } from './clearTracesIfNotEmpty';
import { createBroadcastStreamLifecycle } from './createBroadcastStreamLifecycle';
import { DeepResearchError } from './error-classes/DeepResearchError';
import { NoFinalResponseError } from './error-classes/NoFinalResponseError';
import { findLastAssistantAction } from './findLastAssistantAction';
import { generateId } from './generateId';
import { generateTmpMessageId } from './generateTmpMessageId';
import { getPageContext } from './get-page-context';
import { getViewContext } from './get-view-context';
import { getConversation } from './getConversation';
import { getInteractionModeRequested } from './getInteractionModeRequested';
import { getInvokedFrom } from './getInvokedFrom';
import { getMetricEndHelper } from './getMetricEndHelper';
import { getRemixCreateRequestAttributes } from './getRemixCreateRequestAttributes';
import { getRemixRegenerateRequestAttributes } from './getRemixRegenerateRequestAttributes';
import { getRequestedModeAttributes } from './getRequestedModeAttributes';
import { getSourceControlValueForBackend } from './getSourceControlValueForBackend';
import { getSpaceScopedSourceMode } from './getSpaceScopedSourceMode';
import { handleStreamTaskStatus } from './handleStreamTaskStatus';
import { interactionModeExecutedAttributes } from './interactionModeExecutedAttributes';
import { isEmptyConversation } from './isEmptyConversation';
import { markStreamingMessagesAsComplete } from './markStreamingMessagesAsComplete';
import { mergeSearchBootstrappedAdditionalContext } from './mergeSearchBootstrappedAdditionalContext';
import { populateSendMessageContext } from './populateSendMessageContext';
import { processStream } from './processStream';
import { applyResultAttributionContext } from './resultAttributionContext';
import type {
	ChatStoreActionsForSendMessage,
	SendMessageParams,
	SendMessageTrackingAttributes,
	StreamActionInfo,
} from './sendMessageActionTypes';
import { createStoreAbortController } from './store-abort-controller';
import type { ChatStoreAction, ChatStoreContainerProps, MessageSource } from './types';
import { userSubmissionLifecycle } from './user-submission-lifecycle';

type SendMessageExperienceNameParamsWithTaskMode = {
	deepResearchEnabled?: boolean;
	thinkDeeperEnabled?: boolean;
	taskModeEnabled?: boolean;
	fastModeEnabled?: boolean;
	isCustomRovoAgent?: boolean;
};

const getSendMessageExperienceName = (
	params: SendMessageExperienceNameParamsWithTaskMode,
): ExperienceNameType => {
	if (params.isCustomRovoAgent) {
		return ExperienceName.SEND_MESSAGE_ROVO_CUSTOM_AGENT;
	}

	if (params.deepResearchEnabled) {
		return ExperienceName.SEND_DR_MESSAGE;
	}

	if (params.thinkDeeperEnabled) {
		return ExperienceName.SEND_TD_MESSAGE;
	}

	if (params.taskModeEnabled) {
		return ExperienceName.SEND_TASK_MODE_MESSAGE;
	}

	return ExperienceName.SEND_MESSAGE;
};

const getInitialTrackingAttributes: (
	agentId: string,
	instrumentationId: string,
	deepResearchEnabled: boolean | undefined,
	thinkDeeperEnabled: boolean | undefined,
	fastModeEnabled: boolean | undefined,
	taskModeEnabled: boolean | undefined,
	webSearchEnabled: boolean | undefined,
	messageSources:
		| {
				interactionSource?: InteractionSource;
				followUpSource?: FollowUpSource;
				invokedFrom?: string;
				aiFeatureName?: string;
		  }
		| undefined,
	isFirstPrompt: boolean,
	prompt: DocNode,
	files: FileUploadResponseItem[] | undefined,
	appFilters: RovoApp[] | undefined,
	selectedCloudIds: string[] | undefined,
	template: {
		templateId?: string;
		templateType?: string;
	},
	forcedContentType: string | undefined,
) => SendMessageTrackingAttributes = (
	agentId: string,
	instrumentationId: string,
	deepResearchEnabled: boolean | undefined,
	thinkDeeperEnabled: boolean | undefined,
	fastModeEnabled: boolean | undefined,
	taskModeEnabled: boolean | undefined,
	webSearchEnabled: boolean | undefined,
	messageSources:
		| {
				interactionSource?: InteractionSource;
				followUpSource?: FollowUpSource;
				invokedFrom?: string;
				aiFeatureName?: string;
		  }
		| undefined,
	isFirstPrompt: boolean,
	prompt: DocNode,
	files: FileUploadResponseItem[] | undefined,
	appFilters: RovoApp[] | undefined,
	selectedCloudIds: string[] | undefined,
	template: { templateId?: string; templateType?: string },
	forcedContentType: string | undefined,
) => {
	return {
		pluginsInvoked: new Set(),
		skillsInvoked: [],
		executedStratusSkill: false,
		hasSources: false,
		hasAuthoritativeSources: false,
		messageId: '',
		humanMessageId: '',
		requestId: undefined,
		messageActions: [],
		messageDynamicUIData: [],
		invokedFrom: undefined,
		instrumentationId,
		messageSentTimeStamp: new Date().toISOString(),
		citedProductList: {},
		sourceProducts: {},
		deepResearchEnabled,
		thinkDeeperEnabled,
		fastModeEnabled,
		taskModeEnabled,
		webSearchEnabled,
		interactionSource: messageSources?.interactionSource,
		followUpSource: messageSources?.followUpSource ?? 'none',
		// Assigned by the caller from `conversation.chatEntryPoint` once the conversation is known.
		chatEntryPoint: undefined,
		aiFeatureName: messageSources?.aiFeatureName,
		isFirstPrompt,
		promptFeatures: extractContentFeatures(prompt),
		fileCount: files?.length ?? 0,
		appFilters: appFilters ?? [],
		appFilterCount: appFilters?.length ?? 0,
		selectedCloudIds: selectedCloudIds ?? [],
		selectedCloudIdCount: selectedCloudIds?.length ?? 0,
		template,
		deepResearchInvoked: false,
		forcedContentType,
		interactionModeRequested: getInteractionModeRequested({
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			taskModeEnabled,
		}),
		twgTraceStepIds: [],
		twgTraceRoundIds: [],
		twgTraceToolNames: [],
		twgTraceRenderModes: [],
	};
};

const getOptimisticMessageTypes = (
	deepResearchEnabled: boolean | undefined,
	webSearchEnabled: boolean | undefined,
) => {
	const messageType: MessageType[] = [];

	if (fg('rovo_chat_chat_history_refresh')) {
		if (deepResearchEnabled) {
			messageType.push('DEEP_RESEARCH');
		}
		if (webSearchEnabled) {
			messageType.push('WEB_SEARCH');
		}
	}

	return { messageType };
};

const initLatencyMetrics = ({
	deepResearchEnabled,
	thinkDeeperEnabled,
	fastModeEnabled,
	agentId,
}: {
	deepResearchEnabled: boolean | undefined;
	thinkDeeperEnabled: boolean | undefined;
	fastModeEnabled: boolean | undefined;
	agentId: string | undefined;
}) => {
	const rovoCustomAgent = isRovoCustomAgent(agentId);
	const sendMessageLatencyMetrics = messageLatencyMetrics.createSendMessageMetrics({
		deepResearchEnabled,
		thinkDeeperEnabled,
		fastModeEnabled,
		isRovoCustomAgent: rovoCustomAgent,
	});
	const timeToFirstAnswerPartLatencyMetrics =
		messageLatencyMetrics.createTimeToFirstAnswerPartMetrics({
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			isRovoCustomAgent: rovoCustomAgent,
		});
	const timeToFirstActionPartLatencyMetrics =
		messageLatencyMetrics.createTimeToFirstActionPartMetrics({
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			isRovoCustomAgent: rovoCustomAgent,
		});
	const timeToActionCompletionLatencyMetrics =
		messageLatencyMetrics.createTimeToActionCompletionMetrics({
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			isRovoCustomAgent: rovoCustomAgent,
		});

	const metricsStartTime = window.performance.now();
	sendMessageLatencyMetrics.start({ startTime: metricsStartTime });
	timeToFirstAnswerPartLatencyMetrics.start({ startTime: metricsStartTime });

	timeToFirstActionPartLatencyMetrics.start();
	timeToActionCompletionLatencyMetrics.start();

	const stopInteractionMetricsTimeToFirstAnswerPart = getMetricEndHelper(
		timeToFirstAnswerPartLatencyMetrics,
	);
	const stopInteractionMetricsTimeToFirstActionPart = getMetricEndHelper(
		timeToFirstActionPartLatencyMetrics,
	);
	const stopInteractionMetricsTimeToActionCompletion = getMetricEndHelper(
		timeToActionCompletionLatencyMetrics,
	);

	const cancelStreamingMetrics = () => {
		stopInteractionMetricsTimeToFirstAnswerPart('cancel');
		stopInteractionMetricsTimeToFirstActionPart('cancel');
		stopInteractionMetricsTimeToActionCompletion('cancel');
	};

	return {
		sendMessageLatencyMetrics,
		timeToFirstAnswerPartLatencyMetrics,
		timeToFirstActionPartLatencyMetrics,
		timeToActionCompletionLatencyMetrics,
		stopInteractionMetricsTimeToFirstAnswerPart,
		stopInteractionMetricsTimeToFirstActionPart,
		stopInteractionMetricsTimeToActionCompletion,
		cancelStreamingMetrics,
		metricsStartTime,
	};
};

const deriveOptimisticAssistantAuthorIdentity = ({
	messageAgentIdentity,
	messageRoutingAgentId,
}: {
	messageAgentIdentity?: MessageAgentIdentity;
	messageRoutingAgentId: string;
}) => {
	if (!messageAgentIdentity?.agentId) {
		return { agentAuthorId: messageRoutingAgentId };
	}

	return {
		agentAuthorId: messageAgentIdentity.agentId,
		agentAuthorIdentityAccountId: messageAgentIdentity.identityAccountId,
	};
};

const hydrateStoreWithNewMessage = (
	dispatch: <T extends Action<ChatStorePrivateState, ChatStoreContainerProps, unknown>>(
		actionThunk: T,
	) => ReturnType<T>,
	conversationId: string,
	messageType: MessageType[],
	tmpHumanMessageId: string,
	tmpAssistantMessageId: string,
	prompt: DocNode,
	files: FileUploadResponseItem[] | undefined,
	displayContext: HumanMessageDisplayContext | undefined,
	sourceAgentIdForTemporaryLocalConversation: string,
	setConversationData: ChatStoreActionsForSendMessage['setConversationData'],
	addHumanMessage: ChatStoreActionsForSendMessage['addHumanMessage'],
	addAssistantMessage: ChatStoreActionsForSendMessage['addAssistantMessage'],
	clearFollowUpMessages: ChatStoreActionsForSendMessage['clearFollowUpMessages'],
	messageRoutingAgentId: string,
	messageAgentIdentity?: MessageAgentIdentity,
	followUpAgentIdentity?: SendMessageParams['followUpAgentIdentity'],
	entryPoint?: string,
	getActiveAssistantMessageId?: () => string,
) => {
	const { abortController, storeAbortController, confirmAbort } = createStoreAbortController(
		getActiveAssistantMessageId,
	);
	if (isExperimentEnabled('platform_editor_inline_agentic_comments')) {
		setConversationCancelled(conversationId, false);
	}

	const updateCurrentConversation = (data: Partial<Conversation>) => {
		dispatch(
			setConversationData({
				conversationId,
				data,
			}),
		);
	};

	const mentionAgentsEnabled = isExperimentEnabled('rovo_chat_mention_agents');
	const retryAgentIdentity =
		mentionAgentsEnabled &&
		fg('sltns-1529-draft-agent-non-consequential') &&
		messageAgentIdentity?.agentId
			? {
					id: messageAgentIdentity.agentId,
					...(messageAgentIdentity.identityAccountId
						? { identityAccountId: messageAgentIdentity.identityAccountId }
						: {}),
				}
			: undefined;
	const gatedFollowUpAgentIdentity = mentionAgentsEnabled
		? (followUpAgentIdentity ?? retryAgentIdentity)
		: undefined;
	const optimisticAssistantIdentity = mentionAgentsEnabled
		? deriveOptimisticAssistantAuthorIdentity({
				messageAgentIdentity,
				messageRoutingAgentId,
			})
		: {
				// Remove this named-id control branch with `rovo_chat_mention_agents` cleanup.
				agentNamedId: sourceAgentIdForTemporaryLocalConversation,
			};

	updateCurrentConversation({
		status: CHAT_STORE_STATUS.awaitingResponse,
		storeAbortController,
		lastMessageType: messageType,
		lastAgentMessageStatus: 'IN_PROGRESS',
	});
	dispatch(
		addHumanMessage({
			id: tmpHumanMessageId,
			content: prompt,
			conversationId,
			fileUploads: files,
			displayContext,
			...(gatedFollowUpAgentIdentity ? { followUpAgentIdentity: gatedFollowUpAgentIdentity } : {}),
		}),
	);
	dispatch(
		addAssistantMessage({
			id: tmpAssistantMessageId,
			content: '',
			conversationId,
			...optimisticAssistantIdentity,
			entryPoint,
		}),
	);
	dispatch(clearFollowUpMessages({ conversationId }));

	return { abortController, storeAbortController, confirmAbort, updateCurrentConversation };
};

const handleSendMessageServiceCallFailure = (
	error: unknown,
	deepResearchEnabled: boolean | undefined,
	thinkDeeperEnabled: boolean | undefined,
	fastModeEnabled: boolean | undefined,
	webSearchEnabled: boolean | undefined,
	streamActionInfo: StreamActionInfo,
	chatContextStoreActions: ChatStoreContainerProps['chatContextStoreActions'],
	experienceTracker: ExperienceTrackerAPI,
	sendMessageExperienceNameCompiled: string,
	trackingAttributes: SendMessageTrackingAttributes,
	updateCurrentConversation: (data: Partial<Conversation>) => void,
	sendMessageLatencyMetrics: Metric,
	cancelStreamingMetrics: () => void,
	broadcastStreamLifecycle: (status: StreamLifecycleState) => void,
	confirmAbort: () => void,
	trackAIMAUEvent: (event: TrackAIEventPayloads) => void,
	messageSources: Partial<MessageSource> | undefined,
	abortController: AbortController,
	hasLoggedInitiatedEvent: boolean,
	tmpAssistantMessageId: string,
	clearMessageResearch: (args_0: { messageId: string }) => void,
	getState: () => ChatStorePrivateState,
	conversationId: string,
	storeUserMessages: boolean,
	remixRegenerateRequestAttributes: ReturnType<typeof getRemixRegenerateRequestAttributes>,
) => {
	const apiError = error instanceof ApiError ? error : undefined;
	const normalizedError = error instanceof Error ? error : new Error(String(error));

	// Convert deep research related errors to DeepResearchError when deep research is enabled
	let convertedError: unknown = error;
	if (deepResearchEnabled) {
		convertedError = new DeepResearchError(normalizedError.message, {
			traceId: apiError?.errorAttributes?.traceId,
			requestId: apiError?.errorAttributes?.requestId,
		});
	}

	// Returns bad request error because agent is deactivated or user has no permission
	const isAgentDeactivated =
		error instanceof ApiError &&
		error.errorAttributes.errorCode?.includes(ASSISTANCE_SERVICE_ERROR_CODES.AGENT_DEACTIVATED);
	const isNoAgentPermission =
		error instanceof ApiError &&
		error.errorAttributes.errorCode?.includes(ASSISTANCE_SERVICE_ERROR_CODES.AGENT_FORBIDDEN) &&
		fg('convo-ai-enable-agents-use-permission');

	if (isAgentDeactivated || isNoAgentPermission) {
		experienceTracker.fail({
			name: sendMessageExperienceNameCompiled,
			error: normalizedError,
			attributes: {
				conversationId,
				deepResearchEnabled,
				thinkDeeperEnabled,
				fastModeEnabled,
				webSearchEnabled,
				llmDebugLogsEnabled: storeUserMessages,
				isStreamAction: streamActionInfo.isAction,
				actionKey: streamActionInfo.actionKey,
				featureArea: chatContextStoreActions.getIsStagingArea() ? 'aifc-staging-area' : 'unknown',
				...remixRegenerateRequestAttributes,
			},
		});

		if (!hasLoggedInitiatedEvent) {
			// See: https://hello.atlassian.net/wiki/spaces/~321863207/pages/4188127855/Analytics+2024-11-16
			// Delaying the initiated event firing allows us to create the pairing appropriately,
			// for any initiated events leading up to an error or dismiss state with a
			// hashed ID
			trackAIMAUEvent({
				type: 'initiated',
				subjectId: messageSources?.interactionSource,
				proactive: false,
				userGenerated: true,
				timestamp: trackingAttributes.messageSentTimeStamp,
				singleInstrumentationID: trackingAttributes.instrumentationId,
				deepResearchEnabled: trackingAttributes.deepResearchEnabled,
				thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
				fastModeEnabled: trackingAttributes.fastModeEnabled,
				webSearchEnabled: trackingAttributes.webSearchEnabled,
				pageContext: trackingAttributes.pageContext,
				viewContext: trackingAttributes.viewContext,
				attributes: {
					...getRequestedModeAttributes(trackingAttributes),
					...(trackingAttributes.chatEntryPoint
						? { chatEntryPoint: trackingAttributes.chatEntryPoint }
						: {}),
				},
			});
		}

		trackAIMAUEvent({
			type: 'error',
			userGenerated: true,
			messageId: trackingAttributes.requestId,
			timestamp: new Date().toISOString(),
			singleInstrumentationID: trackingAttributes.instrumentationId,
			deepResearchEnabled: trackingAttributes.deepResearchEnabled,
			thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
			fastModeEnabled: trackingAttributes.fastModeEnabled,
			webSearchEnabled: trackingAttributes.webSearchEnabled,
			attributes: getRequestedModeAttributes(trackingAttributes),
		});

		updateCurrentConversation({
			status: CHAT_STORE_STATUS.deactivatedAgentError,
			traces: [],
			errorCode: fg('convo-ai-enable-agents-use-permission')
				? apiError?.errorAttributes.errorCode
				: undefined,
		});

		sendMessageLatencyMetrics.cancel();
		cancelStreamingMetrics();
		broadcastStreamLifecycle(StreamLifecycleState.ERRORED);
		return;
	} else if (error instanceof Error && error.name === 'AbortError') {
		// Check if the abort was programmatic or not. `abortController.signal.aborted` is a readonly field that is only set to true when called programmatically
		// The browser can abort requests to free up resources & save power (esp. on background tabs). When it does this, it **does not** set `abortController.signal.aborted` to true
		const isProgrammaticAbort = abortController.signal.aborted;

		experienceTracker.abort({
			name: sendMessageExperienceNameCompiled,
			reason: 'Aborted',
			attributes: {
				featureArea: chatContextStoreActions.getIsStagingArea() ? 'aifc-staging-area' : 'unknown',
				isProgrammaticAbort,
			},
		});

		if (!hasLoggedInitiatedEvent) {
			// See: https://hello.atlassian.net/wiki/spaces/~321863207/pages/4188127855/Analytics+2024-11-16
			// Delaying the initiated event firing allows us to create the pairing appropriately,
			// for any initiated events leading up to an error or dismiss state with a
			// hashed ID
			trackAIMAUEvent({
				type: 'initiated',
				subjectId: messageSources?.interactionSource,
				proactive: false,
				userGenerated: true,
				timestamp: trackingAttributes.messageSentTimeStamp,
				singleInstrumentationID: trackingAttributes.instrumentationId,
				deepResearchEnabled: trackingAttributes.deepResearchEnabled,
				thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
				fastModeEnabled: trackingAttributes.fastModeEnabled,
				webSearchEnabled: trackingAttributes.webSearchEnabled,
				pageContext: trackingAttributes.pageContext,
				viewContext: trackingAttributes.viewContext,
				attributes: {
					...getRequestedModeAttributes(trackingAttributes),
					...(trackingAttributes.chatEntryPoint
						? { chatEntryPoint: trackingAttributes.chatEntryPoint }
						: {}),
				},
			});
		}

		trackAIMAUEvent({
			type: 'dismissed',
			userGenerated: true,
			messageId: trackingAttributes.requestId,
			timestamp: new Date().toISOString(),
			singleInstrumentationID: trackingAttributes.instrumentationId,
			subjectId: trackingAttributes.interactionSource,
			deepResearchEnabled: trackingAttributes.deepResearchEnabled,
			thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
			fastModeEnabled: trackingAttributes.fastModeEnabled,
			webSearchEnabled: trackingAttributes.webSearchEnabled,
			attributes: getRequestedModeAttributes(trackingAttributes),
		});

		updateCurrentConversation(
			clearStatus({ lastAgentMessageStatus: isProgrammaticAbort ? 'CANCELLED' : 'FAILED' }),
		);
		clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);
		clearMessageResearch({ messageId: tmpAssistantMessageId });
		sendMessageLatencyMetrics.cancel();
		cancelStreamingMetrics();
		broadcastStreamLifecycle(StreamLifecycleState.ABORTED);

		confirmAbort();

		return;
	} else {
		experienceTracker.fail({
			name: sendMessageExperienceNameCompiled,
			error: normalizedError,
			attributes: {
				conversationId,
				deepResearchEnabled,
				fastModeEnabled,
				thinkDeeperEnabled,
				webSearchEnabled,
				llmDebugLogsEnabled: storeUserMessages,
				isStreamAction: streamActionInfo.isAction,
				actionKey: streamActionInfo.actionKey,
				featureArea: chatContextStoreActions.getIsStagingArea() ? 'aifc-staging-area' : 'unknown',
				...remixRegenerateRequestAttributes,
			},
		});

		if (!hasLoggedInitiatedEvent) {
			// See: https://hello.atlassian.net/wiki/spaces/~321863207/pages/4188127855/Analytics+2024-11-16
			// Delaying the initiated event firing allows us to create the pairing appropriately,
			// for any initiated events leading up to an error or dismiss state with a
			// hashed ID
			trackAIMAUEvent({
				type: 'initiated',
				subjectId: messageSources?.interactionSource,
				proactive: false,
				userGenerated: true,
				timestamp: trackingAttributes.messageSentTimeStamp,
				singleInstrumentationID: trackingAttributes.instrumentationId,
				deepResearchEnabled: trackingAttributes.deepResearchEnabled,
				thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
				fastModeEnabled: trackingAttributes.fastModeEnabled,
				webSearchEnabled: trackingAttributes.webSearchEnabled,
				pageContext: trackingAttributes.pageContext,
				viewContext: trackingAttributes.viewContext,
				attributes: {
					...getRequestedModeAttributes(trackingAttributes),
					...(trackingAttributes.chatEntryPoint
						? { chatEntryPoint: trackingAttributes.chatEntryPoint }
						: {}),
				},
			});
		}

		trackAIMAUEvent({
			type: 'error',
			userGenerated: true,
			messageId: trackingAttributes.requestId,
			timestamp: new Date().toISOString(),
			singleInstrumentationID: trackingAttributes.instrumentationId,
			deepResearchEnabled: trackingAttributes.deepResearchEnabled,
			thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
			fastModeEnabled: trackingAttributes.fastModeEnabled,
			webSearchEnabled: trackingAttributes.webSearchEnabled,
			attributes: getRequestedModeAttributes(trackingAttributes),
		});

		sendMessageLatencyMetrics.cancel();
		cancelStreamingMetrics();
		broadcastStreamLifecycle(StreamLifecycleState.ERRORED);

		throw convertedError;
	}
};

/**
 * Compute the per-send routing identity for a send.
 *
 * Wraps `deriveAgentSendRequestIdentity` with the shared inputs (default agent,
 * explicit caller agent id, and the draft-only-on-chatInput override rule) so the
 * outer early-handler fallback and the inner per-send path stay in lockstep.
 *
 * With the experiment enabled, `conversationAgentForRequest` is the resolved canonical UUID:
 * Rovo for a new conversation or `conversation.agent.id` for a later turn. The control keeps
 * the legacy caller identity so its request shape is unchanged. See ai-mate AGENTS.md
 * "Rovo Chat agent identity, routing, and display".
 */
const deriveSendRoutingIdentity = ({
	conversationAgentForRequest,
	agentIdParam,
	inputSelectedAgentIds,
	messageSources,
	omitConversationDefaultAgentRequestFields,
}: {
	conversationAgentForRequest: { id: string; named_id?: string };
	agentIdParam?: string;
	inputSelectedAgentIds?: string[];
	messageSources?: Partial<MessageSource>;
	omitConversationDefaultAgentRequestFields?: boolean;
}) =>
	deriveAgentSendRequestIdentity({
		conversationAgent: conversationAgentForRequest,
		defaultAgent: DEFAULT_AGENT,
		explicitAgentId: agentIdParam,
		omitConversationDefaultAgentRequestFields,
		inputSelectedAgentIds,
		// The unsent agent-selector draft only routes when the send originates with
		// `messageSources.interactionSource === 'chatInput'`; cross-surface handoffs
		// (e.g. the Confluence inline Rovo input) must not inherit the sidebar's draft.
		allowSelectedAgentOverride: messageSources?.interactionSource === 'chatInput',
	});

/**
 * Build the optimistic `conversation.agent` metadata used when a send creates the
 * conversation. Prefers the already-stored conversation agent, then the caller's
 * rich agent metadata when it matches the resolved conversation agent id, then a
 * provisional placeholder built from that id (the header hydrates the rest).
 *
 * Under `jira_ai_rendering_a2ui`, a stored agent that is itself a provisional
 * placeholder (blank `creator_type`, seeded before backend metadata hydrated) no
 * longer shadows the caller's full metadata for the same agent: consumers that read
 * `creator_type` — notably the `<details>` preprocessor gate — would otherwise treat
 * a third-party agent as first-party for the whole streamed turn. With the gate off
 * the original precedence applies, stored agent first.
 */
const buildOptimisticConversationAgent = ({
	agent,
	currentConversation,
	conversationAgentId,
}: {
	agent: Agent | undefined;
	currentConversation: Conversation | undefined;
	conversationAgentId: string;
}): AgentMetadata | undefined => {
	const callerAgentConversationMetadata: AgentMetadata | undefined =
		agent?.id === conversationAgentId
			? {
					agent_id: agent.id,
					agent_named_id: agent.external_config_reference ?? agent.named_id,
					agent_name: agent.name,
					creator_type: agent.creator_type,
					identity_account_id: agent.identity_account_id ?? undefined,
					is_default: agent.is_default,
					forge_url: agent.icon ?? undefined,
					execution_config: agent.execution_config,
				}
			: undefined;
	const storedConversationAgent = currentConversation?.agent;
	if (fg('jira_ai_rendering_a2ui') && !storedConversationAgent?.creator_type) {
		return (
			callerAgentConversationMetadata ??
			storedConversationAgent ??
			createProvisionalConversationAgent(conversationAgentId)
		);
	}
	return (
		storedConversationAgent ??
		callerAgentConversationMetadata ??
		createProvisionalConversationAgent(conversationAgentId)
	);
};

export const sendMessageActionProvider: (params: SendMessageParams) => ChatStoreAction =
	({
		prompt,
		autoActionApprovalMode,
		agent,
		agentId: agentIdParam,
		agentIdentityAccountId: agentIdentityAccountIdParam,
		conversationId: conversationIdParam,
		storeUserMessages,
		messageSources,
		files,
		deepResearchEnabled,
		thinkDeeperEnabled,
		alphaExtendedThinkingMode,
		fastModeEnabled,
		taskModeEnabled,
		webSearchEnabled,
		conciergeEnabled,
		sourceMode,
		appFilters,
		selectedCloudIds,
		addCurrentPageAsContextUserPreference,
		spaceSourceCount,
		productKey,
		source,
		promptStreamHook,
		creationContextParams,
		additionalContextReceiverId,
		unsupportedRemixNodeTypes,
		additionalAnalyticAttributes,
		contentContext,
		writingStyleContext,
		minionAlias,
		onTempMessageIdReplacedOverride,
		onNewConversationIdAvailable,
		contentId,
		inlineRovoEditAttemptId,
		openChat,
		openChatOnError,
		agentVersion,
		spaceId,
		searchArtifact,
		skillSelectionSource,
		skipEditorContext,
		editorContextFallback,
		shouldExtractConversationScopes,
		browserDisplayContextActive: submittedBrowserDisplayContextActive,
		parentMessageId,
		requestedSkills,
		editRollback,
		inputSelectedAgentIds,
		messageAgentIdentity,
		followUpAgentIdentity,
		invokedByNodeLocalId,
		setConversationData,
		createConversationOptimistically,
		addHumanMessage,
		addAssistantMessage,
		clearFollowUpMessages,
		setSelectedConversation,
		reconnectToInProgressStream,
	}) =>
	async (
		{ dispatch, getState, setState },
		{
			assistanceService,
			experienceTracker,
			sendAnalyticsEvent: sendAnalyticsEventWithDefaults,
			chatContextStoreActions,
			trackAIMAUEvent: trackAIMAUEventWithDefaults,
			getMessages,
			setMessages,
			processStreamResearch,
			completeAllSteps,
			updateResearchMessageId,
			clearMessageResearch,
			addImplicitResearchPlan,
			resetHasReceivedAnswerPart,
			getChatInitiator,
			processMessageStreamAction,
			processMessageStreamComplete,
			processMessageStreamError,
			processMessageStreamActionClassification,
			broadcastStreamStatus,
			siteId,
			onNewConversationStartingWithSendMessage,
			onTempMessageIdReplaced: containerOnTempMessageIdReplaced,
			onHumanMessageIdReplaced: containerOnHumanMessageIdReplaced,
			browserContextSanitizer,
			getBrowserDisplayContextActive,
			viewDeadAirBroker,
			getIsChatMounted,
			capabilities,
			unifiedTasksActions,
			onSpaceSourceRecommendations,
			onPromptSent,
		},
	) => {
		const isPromptContextDisplayEnabled = fg('rovo_chat_prompt_context_display_render');
		// Capture before a new conversation ID or asynchronous context work changes the composer.
		const browserDisplayContextActive = isPromptContextDisplayEnabled
			? (submittedBrowserDisplayContextActive ??
					getBrowserDisplayContextActive?.(
						conversationIdParam ?? getState().currentConversationId,
					)) === true
			: undefined;
		const mentionAgentsEnabled = isExperimentEnabled('rovo_chat_mention_agents');
		const implicitRovoAgentId = mentionAgentsEnabled ? ROVO_AGENT_UUID : ROVO_AGENT_NAMED_ID;
		// Keep the legacy default-agent object available for all gate-off local behavior while
		// allowing gate-on sends with no caller agent to resolve from conversation state.
		const legacyAgent = agent ?? DEFAULT_AGENT;
		const agentForCurrentSendMetadata = mentionAgentsEnabled ? agent : legacyAgent;
		const initialConversationForRouting = getConversation(
			getState(),
			conversationIdParam ?? getState().currentConversationId,
		);
		const conversationAgentIdForRoutingFallback = resolveConversationAgentIdForRouting({
			conversation: initialConversationForRouting,
			fallbackAgentId: mentionAgentsEnabled ? (agent?.id ?? implicitRovoAgentId) : legacyAgent.id,
		});
		// `sourceAgentId` for the temporary local conversation; per-turn routing is computed by `deriveSendRoutingIdentity`.
		const sourceAgentIdForTemporaryLocalConversation =
			agentIdParam ?? (mentionAgentsEnabled ? (agent?.id ?? implicitRovoAgentId) : legacyAgent.id);
		const { messageType } = getOptimisticMessageTypes(deepResearchEnabled, webSearchEnabled);

		let pageContext: string | undefined;

		// Snapshot captured right before the optimistic add when `editRollback` is set,
		// so that a terminal failure can restore the conversation to its pre-edit state
		// by slicing back to this length and re-appending `editRollback.restoreMessages`.
		let rollbackSnapshot: { conversationId: string; lengthBeforeOptimistic: number } | undefined;

		const performEditRollback = () => {
			if (!editRollback || !rollbackSnapshot) {
				return;
			}
			const { conversationId: rollbackConvId, lengthBeforeOptimistic } = rollbackSnapshot;
			const current = getMessages(rollbackConvId);
			setMessages(rollbackConvId, [
				...current.slice(0, lengthBeforeOptimistic),
				...editRollback.restoreMessages,
			]);
		};

		const sendMessageFn = async ({ conversationId }: { conversationId: string }) => {
			const getContentIdForAskQuestionAttributes = (): string | undefined =>
				contentId === undefined ? undefined : String(contentId);

			const getResultAttributesForAskQuestion = () => {
				if (!expValEquals('cc-disambiguation-in-cwr', 'isEnabled', true)) {
					return undefined;
				}

				const confluenceContentId = getContentIdForAskQuestionAttributes();
				if (!confluenceContentId) {
					return undefined;
				}

				return getConfluenceResultAcceptanceAttributesForAskQuestion({
					messages: getMessages(conversationId),
					contentId: confluenceContentId,
				});
			};

			const currentConversation = getConversation(getState(), conversationId);

			// --- Attribution: merged into every analytics event fired below, for this send only. ---
			//
			// conversationChannelId is always attached. rovoJourneyId is attached only for the
			// five-event allowlist (enforced centrally by isRovoJourneyAllowlistedEvent), and is read
			// off `conversationId` specifically -- never the currently active conversation -- because
			// this send can resolve after the user has switched to a different conversation, and the
			// event must still attribute back to the conversation it actually belongs to.
			const getAttributionAttributes = (eventPayload: {
				action?: string;
				actionSubject?: string;
				type?: string;
			}) => {
				const rovoJourneyId = isRovoJourneyAllowlistedEvent(eventPayload)
					? getConversation(getState(), conversationId)?.rovoJourneyId
					: undefined;

				return {
					conversationChannelId: conversationId,
					...(rovoJourneyId ? { rovoJourneyId } : {}),
				};
			};

			// Forwards to the real sendAnalyticsEvent, preserving arity: existing consumers and test
			// doubles distinguish a one-argument call from an explicit second `undefined` argument.
			const forwardAnalyticsEvent = (
				payload: Parameters<ChatStoreContainerProps['sendAnalyticsEvent']>[0],
				options: Parameters<ChatStoreContainerProps['sendAnalyticsEvent']>[1],
			) => {
				if (options === undefined) {
					sendAnalyticsEventWithDefaults(payload);
					return;
				}

				sendAnalyticsEventWithDefaults(payload, options);
			};

			// --- Decorated dispatchers. Everything below this point in the action calls these, not
			// the raw sendAnalyticsEventWithDefaults / trackAIMAUEventWithDefaults above. ---
			const sendAnalyticsEvent: ChatStoreContainerProps['sendAnalyticsEvent'] = (
				payload,
				options,
			) => {
				const eventPayload = isUIAnalyticsEvent(payload) ? payload.payload : payload;
				const attributionAttributes = getAttributionAttributes(eventPayload ?? {});

				if (isUIAnalyticsEvent(payload)) {
					const attributedEvent =
						payload.update({
							...(payload.payload ?? {}),
							attributes: {
								...(payload.payload?.attributes ?? {}),
								...attributionAttributes,
							},
						}) ?? payload;
					forwardAnalyticsEvent(attributedEvent, options);
					return;
				}

				forwardAnalyticsEvent(
					{
						...payload,
						attributes: {
							...payload.attributes,
							...attributionAttributes,
						},
					},
					options,
				);
			};
			const trackAIMAUEvent = (event: TrackAIEventPayloads) => {
				trackAIMAUEventWithDefaults({
					...event,
					attributes: {
						...(typeof event.attributes === 'object' && event.attributes !== null
							? event.attributes
							: {}),
						...getAttributionAttributes(event),
					},
				});
			};
			const isCreateWithRovoSend =
				creationContextParams?.source === 'ai-first-creation' ||
				creationContextParams?.source === 'chat-only-creation-modal' ||
				creationContextParams?.experience === 'cwr' ||
				creationContextParams?.experience === 'cwr_type' ||
				creationContextParams?.experience === 'cwr_edit';
			// Resolve Space context before optimistic creation so the local chat-mode store and
			// backend request use the same source mode for entity-driven empty-chat launches.
			// CWR should not send within a space.
			const resolvedSpaceId =
				spaceId ??
				(!isCreateWithRovoSend
					? (
							chatContextStoreActions.getAIFeatureContext()?.projectContext as
								| { spaceId?: string }
								| undefined
						)?.spaceId
					: undefined);
			const spaceScopedSourceMode = getSpaceScopedSourceMode({
				sourceMode,
				spaceId: resolvedSpaceId ?? currentConversation?.rovoSpaceId,
			});
			const currentConversationAgentId = resolveConversationAgentIdForRouting({
				conversation: currentConversation,
				fallbackAgentId: conversationAgentIdForRoutingFallback,
			});
			const conversationAgentForOptimisticCreation = buildOptimisticConversationAgent({
				agent,
				currentConversation,
				conversationAgentId: currentConversationAgentId,
			});
			const {
				routedSelectedAgentId,
				routingAgentId: messageRoutingAgentId,
				agentIdForRequest,
				agentNamedIdForRequest,
			} = deriveSendRoutingIdentity({
				conversationAgentForRequest: mentionAgentsEnabled
					? {
							id: agent?.id ?? currentConversationAgentId,
						}
					: {
							id: legacyAgent.id,
							named_id: legacyAgent.named_id,
						},
				agentIdParam,
				inputSelectedAgentIds,
				messageSources,
				omitConversationDefaultAgentRequestFields: !mentionAgentsEnabled,
			});
			const sendMessageExperienceNameCompiled = getSendMessageExperienceName({
				deepResearchEnabled,
				thinkDeeperEnabled,
				taskModeEnabled,
				fastModeEnabled,
				isCustomRovoAgent: isRovoCustomAgent(messageRoutingAgentId),
			});
			const resolvedAgentVersion = agentVersion ?? currentConversation?.agentVersion;

			if (currentConversation?.storeAbortController) {
				// Silently aborting if there is another operation in progress
				return;
			}

			const tmpHumanMessageId = generateId();

			const tmpAssistantMessageId = generateTmpMessageId(conversationId);
			let activeAssistantMessageId = tmpAssistantMessageId;

			let generator: AsyncGenerator<StreamMessage | ParsedSSEMessage<StreamMessage>> | undefined;
			let responseData: FetchStreamReturn<StreamMessage>['responseData'] | undefined;
			let hasLoggedInitiatedEvent = false;

			const isFirstPrompt = isEmptyConversation(conversationId);

			const contentContextFromStore = chatContextStoreActions.getIsStagingArea()
				? 'staging-area'
				: 'global';
			const contentContextValue = contentContext ?? contentContextFromStore;
			const isSitesSourceFilterEnabled = fg(ROVO_CHAT_SITES_SOURCE_FILTER_GATE);
			const selectedCloudIdsForGatedFeature = isSitesSourceFilterEnabled
				? selectedCloudIds
				: undefined;

			let trackingAttributes: SendMessageTrackingAttributes = getInitialTrackingAttributes(
				messageRoutingAgentId,
				tmpAssistantMessageId,
				deepResearchEnabled,
				thinkDeeperEnabled,
				fastModeEnabled,
				taskModeEnabled,
				webSearchEnabled,
				messageSources,
				isFirstPrompt,
				prompt,
				files,
				appFilters,
				selectedCloudIdsForGatedFeature,
				{
					templateId: creationContextParams?.templateInput?.templateId,
					templateType: creationContextParams?.templateInput?.templateType,
				},
				creationContextParams?.forcedContentType,
			);

			// Read from the conversation so entry-point attribution persists across turns.
			trackingAttributes.chatEntryPoint = currentConversation?.chatEntryPoint;

			// Prepare fields for the temporary local conversation created before the backend returns one.
			const conversationAgentForTemporaryLocalConversation = mentionAgentsEnabled
				? conversationAgentForOptimisticCreation
				: undefined;
			const conversationAgentFieldsForTemporaryLocalConversation =
				conversationAgentForTemporaryLocalConversation
					? { agent: conversationAgentForTemporaryLocalConversation }
					: {};

			// Participant membership is backend-authoritative. We intentionally avoid
			// optimistic participant writes here because matching backend semantics would
			// require duplicating dedupe, edit/fork, branch-pruning, and multi-turn
			// recomputation rules in the frontend.
			if (isFirstPrompt) {
				conversationId = crypto.randomUUID();
				onNewConversationStartingWithSendMessage(conversationId, {
					...(autoActionApprovalMode !== undefined ? { autoActionApprovalMode } : {}),
					deepResearchEnabled,
					thinkDeeperEnabled,
					alphaExtendedThinkingMode,
					fastModeEnabled,
					...(taskModeEnabled ? { planModeEnabled: taskModeEnabled } : {}),
					webSearchEnabled,
					// Agent Concierge (RSA-1436): carry the Concierge toggle across the
					// placeholder -> real conversation migration. Without this the flag is
					// dropped on the first send of a new conversation, which both hides the
					// Concierge bubble and prevents the explicit Concierge opt-in from being sent.
					conciergeEnabled,
					sourceMode: spaceScopedSourceMode,
					useCurrentPageContext: addCurrentPageAsContextUserPreference,
					spaceSourceCount,
					appFilters,
					selectedCloudIds: selectedCloudIdsForGatedFeature ?? [],
				});
				onNewConversationIdAvailable?.(conversationId);
				// Create the conversation with its initial identity; first-turn routing may
				// refine `conversation.agent` below.
				dispatch(
					createConversationOptimistically({
						id: conversationId,
						status: CHAT_STORE_STATUS.awaitingResponse,
						sourceAgentId:
							currentConversation?.sourceAgentId ?? sourceAgentIdForTemporaryLocalConversation,
						lastMessageType: messageType,
						lastAgentMessageStatus: 'IN_PROGRESS',
						resultAttributionContext: currentConversation?.resultAttributionContext,
						chatEntryPoint: currentConversation?.chatEntryPoint,
						// Immediately associate the optimistic conversation with the space.
						...(resolvedSpaceId
							? FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)
								? { rovoSpaceId: resolvedSpaceId }
								: {}
							: {}),
						...(resolvedAgentVersion && fg('rovo_agent_versioning_enabled')
							? { agentVersion: resolvedAgentVersion }
							: {}),
						...conversationAgentFieldsForTemporaryLocalConversation,
					}),
				);
				recordValidMessage();

				// First-turn routing can refine display identity; keep `sourceAgentId` as history.
				const normalizedRoutingAgentId = messageRoutingAgentId || undefined;
				const provisionalRoutedConversationAgent =
					createProvisionalConversationAgent(normalizedRoutingAgentId);
				const routedIdentityAccountId =
					messageAgentIdentity && messageAgentIdentity.agentId === normalizedRoutingAgentId
						? messageAgentIdentity.identityAccountId
						: undefined;
				const routedConversationAgent =
					provisionalRoutedConversationAgent && routedIdentityAccountId
						? {
								...provisionalRoutedConversationAgent,
								identity_account_id: routedIdentityAccountId,
							}
						: provisionalRoutedConversationAgent;
				if (
					mentionAgentsEnabled &&
					routedConversationAgent &&
					normalizedRoutingAgentId !== currentConversationAgentId
				) {
					dispatch(
						setConversationData({
							conversationId,
							data: { agent: routedConversationAgent },
						}),
					);
				}
			}

			persistRecentConversation({
				type: 'message-sent',
				siteId,
				conversationId,
				lastMessageSentAt: Date.now(),
			});

			const {
				sendMessageLatencyMetrics,
				stopInteractionMetricsTimeToFirstAnswerPart,
				stopInteractionMetricsTimeToFirstActionPart,
				stopInteractionMetricsTimeToActionCompletion,
				cancelStreamingMetrics,
				metricsStartTime,
			} = initLatencyMetrics({
				deepResearchEnabled,
				thinkDeeperEnabled,
				fastModeEnabled,
				agentId: messageRoutingAgentId,
			});
			const remixRegenerateRequestAttributes = getRemixRegenerateRequestAttributes({
				creationContextParams,
				messageSources,
			});
			const remixCreateRequestAttributes = getRemixCreateRequestAttributes({
				creationContextParams,
				messageSources,
			});

			experienceTracker.start({
				name: sendMessageExperienceNameCompiled,
				id: conversationId,
				attributes: {
					conversationId,
					interactionSource: messageSources?.interactionSource,
					resendSource: messageSources?.resendSource,
					featureArea: chatContextStoreActions.getIsStagingArea() ? 'aifc-staging-area' : 'unknown',
					...remixRegenerateRequestAttributes,
					...remixCreateRequestAttributes,
				},
			});

			const chatInitiator = getChatInitiator();

			const { isRemixFollowUp, lastAssistantMessageDynamicUiType } = (() => {
				const lastAction = findLastAssistantAction(getMessages(conversationId), () => true);
				if (!lastAction || !isRemixGeneratedFlow(lastAction)) {
					return { isRemixFollowUp: false, lastAssistantMessageDynamicUiType: undefined };
				}
				// BE stores dynamicUiType under `contentType` in action data
				const contentType = (lastAction.data as { contentType?: string } | undefined)?.contentType;
				return {
					isRemixFollowUp: true,
					lastAssistantMessageDynamicUiType: contentType,
				};
			})();

			let sendMessageContext: Awaited<ReturnType<typeof populateSendMessageContext>>;
			try {
				sendMessageContext = await populateSendMessageContext(
					chatContextStoreActions,
					isFirstPrompt,
					source,
					prompt,
					addCurrentPageAsContextUserPreference,
					contentContextValue,
					creationContextParams,
					productKey,
					sendAnalyticsEvent,
					contentId,
					lastAssistantMessageDynamicUiType,
					skipEditorContext,
					isRemixFollowUp,
					searchArtifact,
					additionalContextReceiverId,
					editorContextFallback,
				);
			} catch (error: unknown) {
				experienceTracker.fail({
					name: sendMessageExperienceNameCompiled,
					error: error as Error,
					attributes: {
						conversationId,
						deepResearchEnabled,
						fastModeEnabled,
						thinkDeeperEnabled,
						webSearchEnabled,
						featureArea: chatContextStoreActions.getIsStagingArea()
							? 'aifc-staging-area'
							: 'unknown',
						failureSource: 'populateSendMessageContext',
						...remixRegenerateRequestAttributes,
					},
				});

				// Note: We don't track error in error event as discussed here:
				// https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/341408/overview#comment-2004687398
				sendMessageLatencyMetrics.cancel();
				cancelStreamingMetrics();
				return;
			}

			// For right panel chat, creationContextParams is undefined but experience is computed
			// in populateSendMessageContext. Populate creationContextParams so there's a single
			// source of truth for experience downstream (e.g. processStream validation).
			const effectiveCreationContextParams: CreationContextParams | undefined =
				fg('confluence_aifc_new_live_page_chat_context') &&
				!creationContextParams &&
				sendMessageContext.computedExperience
					? { experience: sendMessageContext.computedExperience }
					: creationContextParams;

			let aiFeatureContextForBackend = sendMessageContext.aiFeatureContext;
			if (isCreateWithRovoSend && sendMessageContext.aiFeatureContext?.projectContext) {
				const { spaceId: _spaceId, ...projectContextWithoutSpaceId } = sendMessageContext
					.aiFeatureContext.projectContext as { spaceId?: string };
				aiFeatureContextForBackend = {
					...sendMessageContext.aiFeatureContext,
					projectContext: projectContextWithoutSpaceId,
				};
			}
			const hasAttachedAnyContext =
				addCurrentPageAsContextUserPreference === true &&
				Boolean(
					sendMessageContext.editorContext ||
					sendMessageContext.whiteboardContext ||
					sendMessageContext.slidesContext ||
					sendMessageContext.databaseContext ||
					sendMessageContext.browserContext ||
					sendMessageContext.dashboardEditorContext ||
					sendMessageContext.dashboardContext ||
					sendMessageContext.aiFeatureContext ||
					sendMessageContext.jiraWorkflowContext,
				);
			const attachedSourceCount = (spaceSourceCount ?? 0) + (hasAttachedAnyContext ? 1 : 0);
			const storeState = chatContextStoreActions.getAllContext();
			trackingAttributes.contextProvided = Object.entries(storeState).reduce(
				(acc, [key, value]) => {
					acc[key as keyof ChatContextStore] = Boolean(value);
					return acc;
				},
				{} as Record<keyof ChatContextStore, boolean>,
			);
			trackingAttributes.contextProvided.editorContext = Boolean(sendMessageContext.editorContext);
			trackingAttributes.contextProvided.whiteboardContext = Boolean(
				sendMessageContext.whiteboardContext,
			);
			trackingAttributes.contextProvided.slidesContext = Boolean(sendMessageContext.slidesContext);
			trackingAttributes.contextProvided.databaseContext = Boolean(
				sendMessageContext.databaseContext,
			);
			trackingAttributes.contextProvided.browserContext = Boolean(
				sendMessageContext.browserContext,
			);
			trackingAttributes.contextProvided.dashboardContext = Boolean(
				sendMessageContext.dashboardContext,
			);
			trackingAttributes.contextProvided.aiFeatureContext = Boolean(
				sendMessageContext.aiFeatureContext,
			);
			trackingAttributes.contextProvided.jiraWorkflowContext = Boolean(
				sendMessageContext.jiraWorkflowContext,
			);
			trackingAttributes.contextProvided.domContext = Boolean(
				sendMessageContext.browserContext?.context?.htmlBody,
			);
			trackingAttributes.contextProvided.selectionContext = Boolean(
				sendMessageContext.browserContext?.context?.selection?.content,
			);

			const eligibleSelectedWorkDisplayContext = buildOptimisticSelectedWorkDisplayContext(
				sendMessageContext.aiFeatureContext,
			);
			const shouldResolveEligibleBrowserContext =
				!eligibleSelectedWorkDisplayContext &&
				sendMessageContext.useCurrentBrowsingContext === true &&
				browserDisplayContextActive === true;
			const eligibleBrowserContext = shouldResolveEligibleBrowserContext
				? resolveMessageStreamBrowserUrl({
						browserContext: sendMessageContext.browserContext,
						browserContextSanitizer,
						product: assistanceService.getConfig().product,
					})
				: undefined;
			const eligibleBrowserUrl = eligibleBrowserContext?.browserUrl;
			const isEligibleBrowserUrlBlocklisted = Boolean(
				eligibleBrowserUrl && browserContextSanitizer?.isBlocklisted(eligibleBrowserUrl),
			);
			const eligibleDisplayContext: HumanMessageDisplayContext | undefined =
				eligibleSelectedWorkDisplayContext ??
				(eligibleBrowserUrl && eligibleBrowserUrl !== 'NO-URL' && !isEligibleBrowserUrlBlocklisted
					? {
							references: [
								{
									type: 'BROWSER_URL',
									browser_url: eligibleBrowserUrl,
								},
							],
						}
					: undefined);
			const optimisticDisplayContext = isPromptContextDisplayEnabled
				? eligibleDisplayContext
				: undefined;
			const promptContextReference = eligibleDisplayContext?.references[0];
			const previousHumanDisplayContext = getMessages(conversationId).filter(isStoreHuman).at(-1)
				?.content.display_context;
			const promptContextDisplayState = getPromptContextDisplayState({
				currentDisplayContext: optimisticDisplayContext,
				previousDisplayContext: previousHumanDisplayContext,
			});

			sendAnalyticsEvent({
				eventType: 'track',
				action: 'sent',
				actionSubject: 'message',
				attributes: {
					timestamp: undefined,
					messageId: trackingAttributes.humanMessageId,
					interactionSource: trackingAttributes.interactionSource,
					resendSource: messageSources?.resendSource,
					followUpSource: trackingAttributes.followUpSource,
					deepResearchEnabled: trackingAttributes.deepResearchEnabled,
					thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
					fastModeEnabled: trackingAttributes.fastModeEnabled,
					maxModeEnabled: trackingAttributes.taskModeEnabled,
					webSearchEnabled: trackingAttributes.webSearchEnabled,
					contextProvided: trackingAttributes.contextProvided,
					initiator: chatInitiator,
					isFirstPrompt: trackingAttributes.isFirstPrompt,
					promptFeatures: trackingAttributes.promptFeatures,
					fileCount: trackingAttributes.fileCount,
					appFilters: trackingAttributes.appFilters,
					appFilterCount: trackingAttributes.appFilterCount,
					attachedSourceCount,
					...(isSitesSourceFilterEnabled
						? {
								selectedCloudIds: trackingAttributes.selectedCloudIds,
								selectedCloudIdCount: trackingAttributes.selectedCloudIdCount,
							}
						: {}),
					has3pApps: trackingAttributes.appFilters?.some((app) => is3pApp(app.friendlyName)),
					template: trackingAttributes.template,
					deepResearchInvoked: trackingAttributes.deepResearchInvoked,
					conversationChannelId: conversationId,
					promptContextDisplayEnabled: isPromptContextDisplayEnabled,
					...(isPromptContextDisplayEnabled || promptContextReference?.type === 'SELECTED_WORK'
						? {
								promptContextType:
									promptContextReference?.type === 'SELECTED_WORK'
										? 'selectedWork'
										: promptContextReference?.type === 'BROWSER_URL'
											? 'browserUrl'
											: 'none',
							}
						: {}),
					...(isPromptContextDisplayEnabled ? { promptContextDisplayState } : {}),
					...(promptContextReference?.type === 'SELECTED_WORK'
						? {
								selectedWorkItemCountBucket: getSelectedWorkItemCountBucket(
									promptContextReference.selected_work.total_count,
								),
							}
						: {}),
				},
			});

			if (editRollback && !rollbackSnapshot) {
				rollbackSnapshot = {
					conversationId,
					lengthBeforeOptimistic: getMessages(conversationId).length,
				};
			}

			// Seed the agent-run-state correlation now that the run's conversationId is final and
			// streaming has not started. Prefer the value threaded through the auto-send flow; fall
			// back to the stash set at chat-new time (the draft → manual-send path). No-op for
			// non-mention runs. Always consume the stash so a stale value can't leak into a later send.
			const stashedInvocationLocalId = consumeAgentRunInvocation();
			const runInvokedByNodeLocalId = invokedByNodeLocalId ?? stashedInvocationLocalId;
			if (runInvokedByNodeLocalId) {
				beginAgentRun(conversationId, runInvokedByNodeLocalId);
			}

			const { abortController, confirmAbort, updateCurrentConversation } =
				hydrateStoreWithNewMessage(
					dispatch,
					conversationId,
					messageType,
					tmpHumanMessageId,
					tmpAssistantMessageId,
					prompt,
					files,
					optimisticDisplayContext,
					sourceAgentIdForTemporaryLocalConversation,
					setConversationData,
					addHumanMessage,
					addAssistantMessage,
					clearFollowUpMessages,
					messageRoutingAgentId,
					messageAgentIdentity,
					followUpAgentIdentity,
					messageSources?.entryPoint,
					() => activeAssistantMessageId,
				);
			const handleTempMessageIdReplaced = (oldId: string, newId: string) => {
				activeAssistantMessageId = newId;
				(onTempMessageIdReplacedOverride || containerOnTempMessageIdReplaced)?.(oldId, newId);
			};

			const clearRetainedUserSubmission = userSubmissionLifecycle.recordLatestUserSubmission(
				{
					conversationId,
					humanMessageId: tmpHumanMessageId,
				},
				{ abortSignal: abortController.signal },
			);

			// Assumed the insert is successful and inserts at the last position
			const answerIndex = getMessages(conversationId).length - 1;

			// Broadcast stream status to content targets for state sharing
			const broadcastStreamLifecycle = createBroadcastStreamLifecycle({
				broadcastStreamStatus,
				chatContextStoreActions,
				contentContextValue,
				targetId: contentId,
				assistantMessageId: tmpAssistantMessageId,
				inlineRovoEditAttemptId,
			});

			broadcastStreamLifecycle(StreamLifecycleState.SEND_CONTEXT_READY);

			// Initialize action tracking
			let streamActionInfo: StreamActionInfo = {
				isAction: false,
			};

			// Create message stream
			try {
				// Derive page context for analytics from the available context objects
				pageContext = getPageContext({
					editorContext: sendMessageContext.editorContext,
					whiteboardContext: sendMessageContext.whiteboardContext,
					databaseContext: sendMessageContext.databaseContext,
					aiFeatureContext: sendMessageContext.aiFeatureContext,
					browserUrl: sendMessageContext.browserContext?.context?.browserUrl,
				});
				// `contentId` is an optional, caller-supplied `SendMessageParams` field: only the
				// inline input box (`bridge-api/handlers`) and the staging-area flows pass one. The
				// chat input and the pubsub/agent-mention routes leave it undefined, so requiring it
				// here suppressed the callback on the sidebar surfaces this exists for. It is passed
				// through as optional payload instead, and consumers that need content identity
				// resolve it from their own product context.
				if (
					onPromptSent &&
					FeatureGates.getExperimentValue<boolean>(
						'platform_teflon_chat_on_prompt_sent',
						'isEnabled',
						false,
					) &&
					typeof pageContext === 'string' &&
					pageContext !== 'unknown'
				) {
					sendAnalyticsEvent({
						eventType: 'operational',
						action: 'onPromptSentInvoked',
						actionSubject: 'aiMate',
						attributes: {
							pageContext,
						},
					});
					const promptContentId = contentId;
					const promptPageContext = pageContext;
					void Promise.resolve()
						.then(() =>
							onPromptSent({
								pageContext: promptPageContext,
								contentId: promptContentId,
								agentIdentityAccountId:
									agentForCurrentSendMetadata?.identity_account_id ?? undefined,
							}),
						)
						.catch((error: unknown) => {
							sendAnalyticsEvent({
								eventType: 'operational',
								action: 'onPromptSentError',
								actionSubject: 'aiMate',
								attributes: {
									failureReason: 'callbackRejected',
									errorName: error instanceof Error ? error.name : undefined,
									pageContext,
								},
							});
						});
				}
				trackingAttributes.pageContext = pageContext;

				trackingAttributes.viewContext = getViewContext({
					aiFeatureContext: sendMessageContext.aiFeatureContext,
				});

				trackingAttributes.invokedFrom = getInvokedFrom({
					messageActionKey: streamActionInfo?.actionKey,
				});

				// Check if the Jira refresh experiment is enabled but default is set to `true` to pass all non-Jira products

				// Omit caller source controls only for sends routed by `routedSelectedAgentId`;
				// see `getSourceControlValueForBackend`.
				const webSearchEnabledForBackend = getSourceControlValueForBackend({
					routedSelectedAgentId,
					value: webSearchEnabled,
				});
				const appFiltersForBackend = getSourceControlValueForBackend({
					routedSelectedAgentId,
					value: appFilters,
				});
				const selectedCloudIdsForBackend = getSourceControlValueForBackend({
					routedSelectedAgentId,
					value: selectedCloudIdsForGatedFeature,
				});
				const sourceModeForBackend = getSourceControlValueForBackend({
					routedSelectedAgentId,
					value: spaceScopedSourceMode,
				});
				// Experiment-on requests are UUID-only. Remove the control-only named-id request
				// field with `rovo_chat_mention_agents` cleanup.
				const isFollowUpSend = mentionAgentsEnabled && followUpAgentIdentity !== undefined;
				const agentIdForBackend = isFollowUpSend ? followUpAgentIdentity.id : agentIdForRequest;
				const agentNamedIdForBackend = mentionAgentsEnabled ? undefined : agentNamedIdForRequest;

				const result = await assistanceService.sendMessageStream({
					message: prompt,
					autoActionApprovalMode,
					agentId: agentIdForBackend,
					...(mentionAgentsEnabled ? {} : { agentNamedId: agentNamedIdForBackend }),
					controller: abortController,
					conversationId,
					storeMessage: storeUserMessages,
					citationsEnabled: true,
					fileIds: files?.map((file) => file.id),
					deepResearchEnabled,
					thinkDeeperEnabled,
					planModeEnabled: taskModeEnabled,
					alphaExtendedThinkingMode,
					fastModeEnabled,
					webSearchEnabled: webSearchEnabledForBackend,
					sourceMode: sourceModeForBackend,
					appFilters: appFiltersForBackend?.map((app) => app.source),
					selectedCloudIds: selectedCloudIdsForBackend,
					...(minionAlias && expVal('cc_contextual_agent_creation_v2', 'isEnabled', false)
						? {
								minionAliasConfig: {
									stratus_config: { minion_alias: minionAlias },
								},
							}
						: {}),
					browserContext: sendMessageContext.browserContext,
					editorContext: sendMessageContext.editorContext,
					whiteboardContext: sendMessageContext.whiteboardContext,
					slidesContext: sendMessageContext.slidesContext,
					databaseContext: sendMessageContext.databaseContext,
					additionalContext: mergeSearchBootstrappedAdditionalContext(
						sendMessageContext.additionalContext,
						source,
					),
					writingStyleContext,
					aiFeatureContext: aiFeatureContextForBackend,
					jiraWorkflowContext: sendMessageContext.jiraWorkflowContext,
					dashboardContext: sendMessageContext.dashboardContext,
					dashboardEditorContext: sendMessageContext.dashboardEditorContext,
					artifactContext: sendMessageContext.artifactContext,
					useCurrentBrowsingContext: sendMessageContext.useCurrentBrowsingContext,
					browserContextSanitizer,
					optInToDeepResearchV2: true,
					optInThinkDeeperMessages: true,
					optInAnsNow: true,
					optInNewToolTraces: true,
					optInNewTeamworkTraceIcons: fg('rovo_chat_enable_twg_trace_team26'),
					optInSSEStream: true,
					...(resolvedAgentVersion ? { agentVersion: resolvedAgentVersion } : {}),
					...(resolvedSpaceId ? { spaceId: resolvedSpaceId } : {}),
					capabilities,
					shouldExtractConversationScopes,
					...(browserDisplayContextActive !== undefined ? { browserDisplayContextActive } : {}),
					parentMessageId,
					requestedSkills,
					// Agent Concierge (RSA-1436): Concierge is an explicit opt-in mode. It is
					// off by default and runs only when the plus-menu toggle is enabled.
					conciergeEnabled,
				});
				generator = result.generator;
				responseData = result.responseData;
			} catch (error: unknown) {
				clearRetainedUserSubmission();
				if (fg('aifc_show_error_ui_for_inline_rovo')) {
					promptStreamHook?.onError?.(
						error as StreamError | StreamActionError,
						tmpAssistantMessageId,
						{
							openChatOnError,
							conversationId,
						},
					);
				}
				performEditRollback();
				handleSendMessageServiceCallFailure(
					error,
					deepResearchEnabled,
					thinkDeeperEnabled,
					fastModeEnabled,
					webSearchEnabled,
					streamActionInfo,
					chatContextStoreActions,
					experienceTracker,
					sendMessageExperienceNameCompiled,
					trackingAttributes,
					updateCurrentConversation,
					sendMessageLatencyMetrics,
					cancelStreamingMetrics,
					broadcastStreamLifecycle,
					confirmAbort,
					trackAIMAUEvent,
					messageSources,
					abortController,
					hasLoggedInitiatedEvent,
					tmpAssistantMessageId,
					clearMessageResearch,
					getState,
					conversationId,
					storeUserMessages,
					remixRegenerateRequestAttributes,
				);
				return;
			}

			const {
				hasReceivedFinalResponse,
				hasLoggedInitiatedEvent: hasLoggedInitiatedEventForStream,
				hasAbortedForImplicitThinkDeeper,
				implicitThinkDeeperSendMessageMetrics,
				implicitThinkDeeperExperienceName,
				guardEventReceived: wasGuardTerminated,
				assistantMessageId,
			} = await processStream({
				prompt,
				generator,
				productKey,
				answerIndex,
				tmpHumanMessageId,
				tmpAssistantMessageId,
				thinkDeeperEnabled,
				fastModeEnabled,
				deepResearchEnabled,
				webSearchEnabled,
				sendMessageExperienceNameCompiled,
				traceId: responseData?.traceId,
				updateCurrentConversation,
				trackingAttributes,
				conversationId,
				getState,
				promptStreamHook,
				openChat,
				openChatOnError,
				experienceTracker,
				setMessages,
				getMessages,
				processStreamResearch,
				processMessageStreamAction,
				processMessageStreamComplete,
				processMessageStreamError,
				processMessageStreamActionClassification,
				onSpaceSourceRecommendations,
				containerOnTempMessageIdReplaced,
				containerOnHumanMessageIdReplaced,
				updateResearchMessageId,
				sendAnalyticsEvent,
				chatContextStoreActions,
				stopInteractionMetricsTimeToFirstActionPart,
				stopInteractionMetricsTimeToFirstAnswerPart,
				stopInteractionMetricsTimeToActionCompletion,
				broadcastStreamLifecycle,
				sendMessageLatencyMetrics,
				cancelStreamingMetrics,
				onTempMessageIdReplacedOverride: handleTempMessageIdReplaced,
				streamActionInfo,
				trackAIMAUEvent,
				messageSources,
				additionalAnalyticAttributes,
				unsupportedRemixNodeTypes,
				hasLoggedInitiatedEvent,
				clearMessageResearch,
				addImplicitResearchPlan,
				resetHasReceivedAnswerPart,
				viewDeadAirBroker,
				creationContextParams: effectiveCreationContextParams,
				minionAlias:
					minionAlias && expVal('cc_contextual_agent_creation_v2', 'isEnabled', false)
						? minionAlias
						: assistanceService.getConfig().minionAlias,
				agentId: messageRoutingAgentId,
				agentNamedId:
					isExperimentEnabled('platform_editor_ai_streaming_ux_experience_m1') &&
					agentForCurrentSendMetadata?.id === messageRoutingAgentId
						? (agentForCurrentSendMetadata.external_config_reference ??
							agentForCurrentSendMetadata.named_id)
						: undefined,
				// Only surface name/identity from the optional `agent` metadata when it
				// actually describes the routed agent. Prefer the explicitly threaded
				// identity account id from the @mention handoff.
				agentName:
					agentForCurrentSendMetadata?.id === messageRoutingAgentId
						? agentForCurrentSendMetadata.name
						: undefined,
				// This guard prevents the default agent identity from leaking into the telepointer when the routed specialist agent object has not hydrated yet.
				agentIdentityAccountId:
					agentIdentityAccountIdParam ??
					(agentForCurrentSendMetadata?.id === messageRoutingAgentId
						? agentForCurrentSendMetadata.identity_account_id
						: undefined),
				// Same guard as the name/identity: only classify the routed agent, so an
				// unhydrated default agent does not flip the telepointer to the third-party treatment.
				isThirdPartyAgent:
					agentForCurrentSendMetadata?.id === messageRoutingAgentId &&
					agentForCurrentSendMetadata.creator_type
						? isForgeAgentByCreatorType(agentForCurrentSendMetadata.creator_type)
						: undefined,
				storeUserMessages,
				skillSelectionSource,
				assistanceService,
				abortController,
				getIsChatMounted,
				metricsStartTime,
				onTaskStatusUpdate: (streamMessage) => {
					if (!unifiedTasksActions || !fg('rovo_chat_enable_tasks')) {
						return;
					}
					handleStreamTaskStatus({
						conversationId,
						taskMessage: streamMessage.message,
						messageId: trackingAttributes.messageId,
						target: buildUnifiedStreamTaskTarget(unifiedTasksActions),
					});
				},
				onTodoSnapshot: ({ todos }) => {
					if (!unifiedTasksActions || !fg('rovo_chat_enable_tasks')) {
						return;
					}
					unifiedTasksActions.setTaskTodoSnapshot({
						conversationId,
						todoSnapshot: { todos },
					});
				},
				onPromptBlocked: (streamMessage) => {
					const chat = getMessages(conversationId);
					const withoutPlaceholder = chat.filter((_, idx) => idx !== answerIndex);
					const guardBlock: StoreGuardBlock = {
						type: 'GUARD_BLOCK',
						id: String(randomIntFromInterval(0, 1000000)),
						time_created: new Date().toISOString(),
						traceId: streamMessage.metadata?.request_id ?? undefined,
						blockType: streamMessage.type,
					};
					setMessages(conversationId, [...withoutPlaceholder, guardBlock]);
				},
				onPromptRedacted: (streamMessage) => {
					// Only update the human message with the redacted prompt.
					// Keep the assistant placeholder — the stream continues with the actual response.
					const chat = getMessages(conversationId);
					let lastHumanIdx = -1;
					chat.forEach((m, i) => {
						if (isStoreHuman(m)) {
							lastHumanIdx = i;
						}
					});
					if (lastHumanIdx === -1) {
						return;
					}
					const newChat = [...chat];
					newChat[lastHumanIdx] = {
						...(chat[lastHumanIdx] as StoreHuman),
						redactedPrompt: streamMessage.message.redacted_prompt,
					};
					setMessages(conversationId, newChat);
				},
				// onResponseRedacted store mutation (set redactedResponse + clear content + traces)
				// now runs inside processStream via applyResponseRedactionToMessage, so it happens
				// in the same batch and cannot be re-populated by a stale getMessages read (PRGD-488).
				onResponseBlocked: (streamMessage) => {
					const chat = getMessages(conversationId);
					const withoutPlaceholder = chat.filter((_, idx) => idx !== answerIndex);
					const guardBlock: StoreGuardBlock = {
						type: 'GUARD_BLOCK',
						id: String(randomIntFromInterval(0, 1000000)),
						time_created: new Date().toISOString(),
						traceId: streamMessage.metadata?.request_id ?? undefined,
						blockType: streamMessage.type,
					};
					setMessages(conversationId, [...withoutPlaceholder, guardBlock]);
				},
				onAttachmentBlocked: (streamMessage) => {
					const chat = getMessages(conversationId);
					const withoutPlaceholder = chat.filter((_, idx) => idx !== answerIndex);
					const guardBlock: StoreGuardBlock = {
						type: 'GUARD_BLOCK',
						id: String(randomIntFromInterval(0, 1000000)),
						time_created: new Date().toISOString(),
						traceId: streamMessage.metadata?.request_id ?? undefined,
						blockType: streamMessage.type,
					};
					setMessages(conversationId, [...withoutPlaceholder, guardBlock]);
				},
			}).finally(clearRetainedUserSubmission);

			hasLoggedInitiatedEvent = hasLoggedInitiatedEventForStream;

			// TODO: This should be behind the !aborted logic, but due to a bug in the BE service, aborted requests
			// will client connection with the server yet still complete in the background. This results in the UI showing
			// no message updates, until reload when the actual resolved message can be sesen.
			// Once that is fixed, we can place this behind the !aborted logic.
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'received',
				actionSubject: 'agentMessage',
				attributes: {
					// messageId must remain the ID received from the server in the FINAL_RESPONSE
					// in order to map correctly to our event funnel, e.g `agentMessage received` -> `agentResponse content copied`
					messageId: trackingAttributes.messageId,
					humanMessageId: trackingAttributes.humanMessageId,
					pluginsInvoked: Array.from(trackingAttributes.pluginsInvoked),
					requestId: trackingAttributes.requestId,
					hasSources: trackingAttributes.hasSources,
					// Non-UGC authoritative-answer descriptors, populated during the
					// stream (gated on rovo_authoritative_answer_attribution).
					...(trackingAttributes.hasAuthoritativeSources
						? {
								hasAuthoritativeSources: true,
								authorityLevel: trackingAttributes.authorityLevel,
								isHighlyAuthoritative: trackingAttributes.isHighlyAuthoritative,
								overallConfidence: trackingAttributes.overallConfidence,
								authoritativeSourceCount: trackingAttributes.authoritativeSourceCount,
								authoritativeExpertCount: trackingAttributes.authoritativeExpertCount,
							}
						: {}),
					citedProductList: trackingAttributes.citedProductList,
					connectorInfo: trackingAttributes.connectorInfo,
					sourceProducts: trackingAttributes.sourceProducts,
					has3pApps: Object.keys(trackingAttributes.sourceProducts ?? {}).some(is3pApp),
					timestamp: new Date().toISOString(),
					messageActions: trackingAttributes.messageActions,
					messageDynamicUIData: trackingAttributes.messageDynamicUIData,
					singleInstrumentationID: trackingAttributes.instrumentationId,
					deepResearchEnabled: trackingAttributes.deepResearchEnabled,
					thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
					fastModeEnabled: trackingAttributes.fastModeEnabled,
					webSearchEnabled: trackingAttributes.webSearchEnabled,
					initiator: chatInitiator,
					deepResearchInvoked: trackingAttributes.deepResearchInvoked,
					scenarioId: trackingAttributes.scenarioId,
					// Lumina content descriptors (see trackingAttributes.hasLumina).
					// Joined on messageId against the
					// `luminaComponent shown` render event to detect Lumina returned
					// by the backend but never rendered.
					...(trackingAttributes.hasLumina
						? {
								hasLumina: true,
								luminaType: trackingAttributes.luminaType,
								luminaCount: trackingAttributes.luminaCount,
							}
						: {}),
					...(trackingAttributes.personEntityAnnotationCount !== undefined ||
					trackingAttributes.inferredProjectEntityAnnotationCount !== undefined
						? {
								personEntityAnnotationCount: trackingAttributes.personEntityAnnotationCount,
								inferredProjectEntityAnnotationCount:
									trackingAttributes.inferredProjectEntityAnnotationCount,
							}
						: {}),
				},
			});

			const wasProgrammaticallyAborted = abortController.signal.aborted;
			updateCurrentConversation(
				clearStatus({
					lastAgentMessageStatus: wasProgrammaticallyAborted ? 'CANCELLED' : 'SUCCESSFUL',
				}),
			);

			setMessages(conversationId, markStreamingMessagesAsComplete(getMessages(conversationId)));

			if (abortController.signal.aborted) {
				if (!hasLoggedInitiatedEvent) {
					// See: https://hello.atlassian.net/wiki/spaces/~321863207/pages/4188127855/Analytics+2024-11-16
					// Delaying the initiated event firing allows us to create the pairing appropriately,
					// for any initiated events leading up to an error or dismiss state with a
					// hashed ID
					trackAIMAUEvent({
						type: 'initiated',
						subjectId: messageSources?.interactionSource,
						proactive: false,
						userGenerated: true,
						timestamp: trackingAttributes.messageSentTimeStamp,
						singleInstrumentationID: trackingAttributes.instrumentationId,
						deepResearchEnabled: trackingAttributes.deepResearchEnabled,
						thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
						fastModeEnabled: trackingAttributes.fastModeEnabled,
						webSearchEnabled: trackingAttributes.webSearchEnabled,
						pageContext: trackingAttributes.pageContext,
						viewContext: trackingAttributes.viewContext,
						attributes: {
							...getRequestedModeAttributes(trackingAttributes),
							...(trackingAttributes.chatEntryPoint
								? { chatEntryPoint: trackingAttributes.chatEntryPoint }
								: {}),
						},
					});
				}

				trackAIMAUEvent({
					type: 'dismissed',
					userGenerated: true,
					messageId: trackingAttributes.messageId,
					singleInstrumentationID: trackingAttributes.instrumentationId,
					subjectId: trackingAttributes.interactionSource,
					deepResearchEnabled: trackingAttributes.deepResearchEnabled,
					thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
					fastModeEnabled: trackingAttributes.fastModeEnabled,
					webSearchEnabled: trackingAttributes.webSearchEnabled,
					attributes: {
						...getRequestedModeAttributes(trackingAttributes),
						...(messageSources?.entryPoint && { entryPoint: messageSources.entryPoint }),
					},
				});

				confirmAbort();
				broadcastStreamLifecycle(StreamLifecycleState.ABORTED);

				// Call the cancel API if we have a message ID
				if (trackingAttributes.messageId) {
					assistanceService
						.cancelMessageStream({
							conversationId,
							messageId: trackingAttributes.messageId,
						})
						.catch((error: unknown) => {
							// Log but don't throw - the abort is already happening
							logException({
								name: 'rovoCancelMessageStream',
								exception: error,
								tags: {
									conversationId,
									messageId: trackingAttributes.messageId,
								},
							});
						});
				}

				// For implicit think-deeper, the auto-mode experience was already aborted inside processStream;
				// abort the re-attributed think-deeper experience instead.
				experienceTracker.abort({
					name: hasAbortedForImplicitThinkDeeper
						? (implicitThinkDeeperExperienceName ?? ExperienceName.SEND_TD_MESSAGE)
						: sendMessageExperienceNameCompiled,
					reason: 'Aborted',
					attributes: {
						featureArea: chatContextStoreActions.getIsStagingArea()
							? 'aifc-staging-area'
							: 'unknown',
						isProgrammaticAbort: true, // always true because we're within abortController.signal.aborted (further explained in similar abort for AbortError case)
					},
				});
				experienceTracker.abort({
					name: ExperienceName.AIFC_MESSAGE,
					reason: 'stream aborted',
				});
				completeAllSteps({ messageId: assistantMessageId });
				return;
			}

			// Only fire if the request wasn't aborted
			else {
				if (!hasLoggedInitiatedEvent) {
					// See: https://hello.atlassian.net/wiki/spaces/~321863207/pages/4188127855/Analytics+2024-11-16
					// Delaying the initiated event firing allows us to create the pairing appropriately,
					// for any initiated events leading up to an error or dismiss state with a
					// hashed ID
					trackAIMAUEvent({
						type: 'initiated',
						subjectId: messageSources?.interactionSource,
						proactive: false,
						userGenerated: true,
						timestamp: trackingAttributes.messageSentTimeStamp,
						singleInstrumentationID: trackingAttributes.instrumentationId,
						deepResearchEnabled: trackingAttributes.deepResearchEnabled,
						thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
						fastModeEnabled: trackingAttributes.fastModeEnabled,
						webSearchEnabled: trackingAttributes.webSearchEnabled,
						pageContext: trackingAttributes.pageContext,
						viewContext: trackingAttributes.viewContext,
						attributes: {
							...getRequestedModeAttributes(trackingAttributes),
							...(trackingAttributes.chatEntryPoint
								? { chatEntryPoint: trackingAttributes.chatEntryPoint }
								: {}),
						},
					});
				}

				//If we're done processing the stream and no answer part has been processed, then it means that the BE is hanging. Throw an error
				if (!hasReceivedFinalResponse && !wasGuardTerminated) {
					const agentDetails = await assistanceService.getAgentDetails(messageRoutingAgentId);
					if (agentDetails.deactivated) {
						// If agent is deactivated, throw correct error
						const agentError = new NoFinalResponseError(
							'The selected agent may no longer be available.',
							{
								traceId: responseData?.traceId,
							},
						);

						experienceTracker.fail({
							name: sendMessageExperienceNameCompiled,
							error: agentError,
							attributes: {
								conversationId,
								deepResearchEnabled,
								thinkDeeperEnabled,
								fastModeEnabled,
								webSearchEnabled,
								llmDebugLogsEnabled: storeUserMessages,
								isStreamAction: streamActionInfo.isAction,
								actionKey: streamActionInfo.actionKey,
								featureArea: chatContextStoreActions.getIsStagingArea()
									? 'aifc-staging-area'
									: 'unknown',
								traceId: responseData?.traceId,
								...remixRegenerateRequestAttributes,
							},
						});

						trackAIMAUEvent({
							type: 'error',
							userGenerated: true,
							messageId: trackingAttributes.requestId,
							timestamp: new Date().toISOString(),
							singleInstrumentationID: trackingAttributes.instrumentationId,
							deepResearchEnabled: trackingAttributes.deepResearchEnabled,
							thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
							fastModeEnabled: trackingAttributes.fastModeEnabled,
							webSearchEnabled: trackingAttributes.webSearchEnabled,
							attributes: {
								...(creationContextParams?.dynamicUiType && {
									dynamicUiType: creationContextParams.dynamicUiType,
								}),
								...(creationContextParams?.dynamicUiSubtype && {
									dynamicUiSubtype: creationContextParams.dynamicUiSubtype,
								}),
								...(trackingAttributes?.invokedFrom && {
									invokedFrom: trackingAttributes.invokedFrom,
								}),
								...(streamActionInfo?.actionKey && {
									actionKey: streamActionInfo.actionKey,
								}),
								...getRequestedModeAttributes(trackingAttributes),
							},
						});

						updateCurrentConversation({
							status: CHAT_STORE_STATUS.deactivatedAgentError,
							traces: [],
						});

						sendMessageLatencyMetrics.cancel();
						cancelStreamingMetrics();
						broadcastStreamLifecycle(StreamLifecycleState.ERRORED);
						return;
					} else {
						// Agent is active, BE hanging
						const timeoutError = new NoFinalResponseError(
							'The response took too long and timed out. Try refreshing the page.',
							{
								traceId: responseData?.traceId,
							},
						);

						experienceTracker.fail({
							name: sendMessageExperienceNameCompiled,
							error: timeoutError,
							attributes: {
								conversationId,
								deepResearchEnabled,
								thinkDeeperEnabled,
								fastModeEnabled,
								webSearchEnabled,
								llmDebugLogsEnabled: storeUserMessages,
								isStreamAction: streamActionInfo.isAction,
								actionKey: streamActionInfo.actionKey,
								featureArea: chatContextStoreActions.getIsStagingArea()
									? 'aifc-staging-area'
									: 'unknown',
								traceId: responseData?.traceId,
								...remixRegenerateRequestAttributes,
							},
						});

						trackAIMAUEvent({
							type: 'error',
							userGenerated: true,
							messageId: trackingAttributes.requestId,
							timestamp: new Date().toISOString(),
							singleInstrumentationID: trackingAttributes.instrumentationId,
							deepResearchEnabled: trackingAttributes.deepResearchEnabled,
							thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
							fastModeEnabled: trackingAttributes.fastModeEnabled,
							webSearchEnabled: trackingAttributes.webSearchEnabled,
							attributes: {
								...(creationContextParams?.dynamicUiType && {
									dynamicUiType: creationContextParams.dynamicUiType,
								}),
								...(creationContextParams?.dynamicUiSubtype && {
									dynamicUiSubtype: creationContextParams.dynamicUiSubtype,
								}),
								...(trackingAttributes?.invokedFrom && {
									invokedFrom: trackingAttributes.invokedFrom,
								}),
								...(streamActionInfo?.actionKey && {
									actionKey: streamActionInfo.actionKey,
								}),
								...getRequestedModeAttributes(trackingAttributes),
							},
						});

						sendMessageLatencyMetrics.cancel();
						cancelStreamingMetrics();
						broadcastStreamLifecycle(StreamLifecycleState.ERRORED);

						throw timeoutError;
					}
				}

				/**
				 * IMPORTANT: CRITICAL ANALYTICS EVENT
				 *
				 * This event is essential for business metrics and monitoring.
				 *
				 * DO NOT MODIFY the structure, content, or emission logic of this event without:
				 *   - Careful review of potential impacts on data quality, privacy, and business reporting.
				 *   - Explicit approval from both analytics and security stakeholders.
				 *
				 * Reference: Incident HOT-120175 (UGC page title and URLs leak)
				 * Event Registry: https://data-portal.internal.atlassian.com/analytics/registry/68936?tabIndex=Details
				 */
				const dynamicUiType =
					creationContextParams?.dynamicUiType ?? sendMessageContext?.editorContext?.dynamicUiType;
				const conversationForResultAttribution =
					getConversation(getState(), conversationId) ?? currentConversation;
				const resultAttributionAnalytics = applyResultAttributionContext({
					resultAttributionContext: conversationForResultAttribution?.resultAttributionContext,
					routingAgentId: messageRoutingAgentId,
					messageSources,
					additionalAnalyticAttributes,
				});
				if (resultAttributionAnalytics.shouldClearResultAttributionContext) {
					dispatch(
						setConversationData({
							conversationId,
							data: { resultAttributionContext: undefined },
						}),
					);
				}
				trackAIMAUEvent({
					type: 'viewed',
					subjectId: resultAttributionAnalytics.messageSources?.followUpSource,
					messageId: trackingAttributes.messageId,
					source: resultAttributionAnalytics.messageSources?.interactionSource,
					messageActions: trackingAttributes.messageActions,
					messageDynamicUIData: trackingAttributes.messageDynamicUIData,
					invokedFrom: trackingAttributes.invokedFrom,
					proactive: false,
					userGenerated: true,
					singleInstrumentationID: trackingAttributes.instrumentationId,
					timestamp: new Date().toISOString(),
					citedProductList: trackingAttributes.citedProductList,
					connectorInfo: trackingAttributes.connectorInfo,
					sourceProducts: trackingAttributes.sourceProducts,
					pluginsInvoked: Array.from(trackingAttributes.pluginsInvoked),
					skillsInvoked: trackingAttributes.skillsInvoked,
					executedStratusSkill: trackingAttributes.executedStratusSkill,
					responseSources: trackingAttributes.sources,
					usesJsm: trackingAttributes.usesJsm,
					deepResearchEnabled: trackingAttributes.deepResearchEnabled,
					thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
					fastModeEnabled: trackingAttributes.fastModeEnabled,
					webSearchEnabled: trackingAttributes.webSearchEnabled,
					conversationChannelId: conversationId,
					scenarioId: trackingAttributes.scenarioId,
					pageContext: trackingAttributes.pageContext,
					attributes: {
						...(fg('aifc_request_id_analytics') && trackingAttributes.requestId
							? { request_id: trackingAttributes.requestId }
							: {}),
						...interactionModeExecutedAttributes(trackingAttributes.interactionModeExecuted),
						...(dynamicUiType && { dynamicUiType }),
						...(creationContextParams?.dynamicUiSubtype && {
							dynamicUiSubtype: creationContextParams.dynamicUiSubtype,
						}),
						isTabVisible: getDocument()?.visibilityState === 'visible',
						...(resultAttributionAnalytics.additionalAnalyticAttributes &&
							resultAttributionAnalytics.additionalAnalyticAttributes),
						...(getResultAttributesForAskQuestion() ?? {}),
						...(creationContextParams?.dynamicUiSource && {
							remixNodeTypes: trackingAttributes.remixNodeTypes,
							...(trackingAttributes.unsupportedRemixNodeTypes !== undefined && {
								unsupportedRemixNodeTypes: trackingAttributes.unsupportedRemixNodeTypes,
							}),
							...(trackingAttributes.remixWordCount !== undefined && {
								remixWordCount: trackingAttributes.remixWordCount,
							}),
						}),
						...(trackingAttributes.aiFeatureName && {
							aiFeatureName: trackingAttributes.aiFeatureName,
						}),
						...(resultAttributionAnalytics.messageSources?.entryPoint && {
							entryPoint: resultAttributionAnalytics.messageSources.entryPoint,
						}),
						...(trackingAttributes.chatEntryPoint && {
							chatEntryPoint: trackingAttributes.chatEntryPoint,
						}),
						...(fg('rovo_chat_enable_twg_trace_team26') &&
							trackingAttributes.twgTraceStepIds &&
							trackingAttributes.twgTraceStepIds.length > 0 && {
								twgTraceShown: true,
								twgTraceStepIds: trackingAttributes.twgTraceStepIds,
								twgTraceRoundIds: trackingAttributes.twgTraceRoundIds,
								twgTraceToolNames: trackingAttributes.twgTraceToolNames,
								...(trackingAttributes.twgTraceRenderModes &&
									trackingAttributes.twgTraceRenderModes.length > 0 && {
										twgTraceRenderModes: trackingAttributes.twgTraceRenderModes,
									}),
							}),
						...(fg('add_mci_kr_attributes_to_rovo_chat_analytics')
							? mciKrAttributesFromSkillsAndPlugins(
									trackingAttributes.skillsInvoked,
									trackingAttributes.pluginsInvoked,
									trackingAttributes.agentExternalConfigReference,
								)
							: {}),
					},
				});

				// Experience is aborted in `abortRequests`
				// For implicit think-deeper, succeed the re-attributed think-deeper reliability SLO instead
				experienceTracker.succeed({
					name: hasAbortedForImplicitThinkDeeper
						? (implicitThinkDeeperExperienceName ?? ExperienceName.SEND_TD_MESSAGE)
						: sendMessageExperienceNameCompiled,
				});

				// Broadcast that the stream has completed successfully
				broadcastStreamLifecycle(StreamLifecycleState.COMPLETED);

				if (fg('rovo_chat_chat_history_refresh')) {
					//If user is viewing a different conversation or not viewing ConversationAssitant, optimistically set conversation to unread
					if (getState().currentConversationId !== conversationId) {
						dispatch(
							setConversationData({
								conversationId,
								data: { readStatus: false },
							}),
						);
					}
				}
			}

			// For implicit think-deeper, stop the re-attributed think-deeper metric instead
			if (!hasAbortedForImplicitThinkDeeper) {
				sendMessageLatencyMetrics.stop({
					customData: messageSources?.interactionSource
						? { interactionSource: messageSources.interactionSource }
						: undefined,
				});
			} else {
				implicitThinkDeeperSendMessageMetrics?.stop({
					customData: messageSources?.interactionSource
						? { interactionSource: messageSources.interactionSource }
						: undefined,
				});
			}
		};

		const conversationId = conversationIdParam ?? getState().currentConversationId;

		try {
			await sendMessageFn({ conversationId });
		} catch (error: unknown) {
			if (isChannelNotFoundError(error)) {
				const createdChannel = await assistanceService.createConversationChannel();
				onNewConversationIdAvailable?.(createdChannel.id);

				setState({
					conversations: {
						...getState().conversations,
						[createdChannel.id]: {
							id: createdChannel.id,
							name: createdChannel.name ?? generateConversationName(),
							timeCreated: createdChannel.time_created,
							status: CHAT_STORE_STATUS.idle,
							traces: [],
							messages: [],
							...(createdChannel.agent && fg('rovo_chat_chat_history_refresh')
								? { agent: createdChannel.agent }
								: {}),
						},
					},
				});

				setMessages(createdChannel.id, []);

				dispatch(
					setSelectedConversation({
						conversationId: createdChannel.id,
					}),
				);

				await sendMessageFn({ conversationId: createdChannel.id });
			} else {
				performEditRollback();
				throw error;
			}
		}
	};
